// File: pages/ShuffleMaps/CreateListing.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useUser } from '/context/UserContext';
import styles from './CreateListing.module.css';

// Optional: include site header/footer so language dropdown appears on this page
import Header from '/components/Shufflemaps/Header/Header';
import Footer from '/components/Shufflemaps/Footer/Footer';

// Same i18n JSONs used on the main ShuffleMaps page
import ja from '/components/Shufflemaps/i18n/ja.json';
import ko from '/components/Shufflemaps/i18n/ko.json';
import zh from '/components/Shufflemaps/i18n/zh.json';
import en from '/components/Shufflemaps/i18n/en.json';
import es from '/components/Shufflemaps/i18n/es.json';
import ru from '/components/Shufflemaps/i18n/ru.json';

// Reuse the exact same picker from ListingDetail header
import CategoryPickerMulti from '/components/Shufflemaps/ListingDetail/CategoryPickerMulti';

// Supported translations
const translations = { en, es, ru, ja, ko, zh };

// Minimal i18n helpers (dot-path lookup + {{var}} interpolation)
const getPath = (obj, path) =>
  path.split('.').reduce((o, k) => (o && o[k] != null ? o[k] : null), obj);
const interpolate = (str, params = {}) =>
  typeof str === 'string'
    ? str.replace(/\{\{(\w+)\}\}/g, (_, k) => (params[k] != null ? String(params[k]) : ''))
    : str;

const filterOutOnline = (arr) =>
  Array.isArray(arr) ? arr.filter((s) => String(s).toLowerCase() !== 'online') : [];

const CreateListing = () => {
  const router = useRouter();
  const { status, user } = useUser();

  // Language state mirrors ShuffleMaps.jsx
  const [language, setLanguage] = useState('en');
  const i18n = translations[language] || translations.en;
  const t = (key, params) => interpolate(getPath(i18n, key) || key, params);

  const isAuthed = status === 'authenticated';
  const isLoadingAuth = status === 'loading';

  const [name, setName] = useState('');
  const [categories, setCategories] = useState([]); // slugs from CategoryPickerMulti
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // For UX: prevent double-submits
  const submittedRef = useRef(false);

  useEffect(() => {
    setErr(null);
  }, [name, categories]);

  const canSubmit = useMemo(
    () => isAuthed && name.trim().length > 0 && categories.length > 0 && !submitting,
    [isAuthed, name, categories, submitting]
  );

  const handleCreate = async () => {
    if (!canSubmit || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setErr(null);

    try {
      // 1) Create minimal listing via dedicated endpoint
      const createRes = await fetch('/api/shufflemaps/listings/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim() }),
      });

      if (!createRes.ok) {
        const ttxt = await createRes.text().catch(() => '');
        console.error('Create listing failed:', createRes.status, ttxt);
        throw new Error(ttxt || `Create failed (${createRes.status})`);
      }

      const created = await createRes.json().catch(() => ({}));
      const listingId = created?.id ?? created?.listingId ?? null;
      if (!listingId) throw new Error('Missing new listing id from server');

      // 2) Immediately apply categories (slugs or names accepted)
      const cleanCategories = filterOutOnline(categories);
      if (cleanCategories.length > 0) {
        const catRes = await fetch('/api/shufflemaps/listings/update-listing-categories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ listingId, categories: cleanCategories }),
        });
        if (!catRes.ok) {
          const ttxt = await catRes.text().catch(() => '');
          console.error('Update categories failed:', catRes.status, ttxt);
          throw new Error(`Saved listing but failed to set categories: ${ttxt || catRes.status}`);
        }
      }

      // 3) Forward **within the ShuffleMaps context** (shell reads ?listingId)
      router.push(`/ShuffleMaps?listingId=${listingId}`);
    } catch (e) {
      setErr(e?.message || 'Failed to create listing');
      setSubmitting(false);
      submittedRef.current = false;
    }
  };

  const page = (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header-ish feel to match ListingDetail */}
        <div className={styles.headerRow}>
          <h1 className={styles.title}>{t('createListing.title')}</h1>
          <div
            className={styles.userChip}
            title={t('createListing.loggedInAs', {
              email: user?.email || t('createListing.loggedInAsFallback'),
            })}
          >
            <span className={styles.dot} />{' '}
            <span>{user?.email || t('createListing.loggedInAsFallback')}</span>
          </div>
        </div>

        <p className={styles.helperText}>{t('createListing.intro')}</p>

        {/* Title input (name) */}
        <div className={styles.fieldBlock}>
          <label className={styles.label}>{t('createListing.fieldTitleLabel')}</label>
          <input
            className={styles.input}
            placeholder={t('createListing.fieldTitlePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={140}
          />
          <div className={styles.fieldHint}>
            {t('createListing.fieldTitleCount', { count: name.trim().length })}
          </div>
        </div>

        {/* Categories — reuse header picker; hide "online" */}
        <div className={styles.fieldBlock}>
          <label className={styles.label}>{t('createListing.fieldCategoriesLabel')}</label>
          <CategoryPickerMulti
            valueSlugs={categories}
            onChange={(slugs) => setCategories(filterOutOnline(slugs))}
            excludeSlugs={['online']}
          />
          <div className={styles.fieldHint}>{t('createListing.fieldCategoriesHint')}</div>
        </div>

        {/* Actions */}
        <div className={styles.actionsRow}>
          <button
            className={styles.primaryBtn}
            onClick={handleCreate}
            disabled={!canSubmit}
            aria-busy={submitting ? 'true' : 'false'}
          >
            {submitting ? t('createListing.primaryCreating') : t('createListing.primaryCreate')}
          </button>
          <button
            className={styles.secondaryBtn}
            onClick={() => router.push('/ShuffleMaps')}
          >
            {t('createListing.cancel')}
          </button>
        </div>

        {err && <div className={styles.errorBox}>{err}</div>}

        <div className={styles.footNote}>{t('createListing.footNote')}</div>
      </div>
    </div>
  );

  // Auth gates (keep the header around them so dropdown is available)
  if (isLoadingAuth) {
    return (
      <>
        <Header
          i18n={i18n}
          language={language}
          setLanguage={setLanguage}
          availableLanguages={Object.keys(translations)}
        />
        <div className={styles.container}>
          <div className={styles.card}>{t('createListing.checkingSession')}</div>
        </div>
        <Footer i18n={i18n} />
      </>
    );
  }

  if (!isAuthed) {
    return (
      <>
        <Header
          i18n={i18n}
          language={language}
          setLanguage={setLanguage}
          availableLanguages={Object.keys(translations)}
        />
        <div className={styles.container}>
          <div className={styles.card}>
            <h1 className={styles.title}>{t('createListing.title')}</h1>
            <p className={styles.helperText}>{t('createListing.mustSignInBody')}</p>
            <div className={styles.actionsRow}>
              <a href="/login" className={styles.primaryBtn}>
                {t('createListing.signInCta')}
              </a>
            </div>
            <p className={styles.smallNote}>{t('createListing.signInNote')}</p>
          </div>
        </div>
        <Footer i18n={i18n} />
      </>
    );
  }

  // Main create UI (with header/footer)
  return (
    <>
      <Header
        i18n={i18n}
        language={language}
        setLanguage={setLanguage}
        availableLanguages={Object.keys(translations)}
      />
      {page}
      <Footer i18n={i18n} />
    </>
  );
};

export default CreateListing;

// Optional: mirror main page pattern
CreateListing.getLayout = (page) => page;
